/**
 * `/sheets` — library of saved spreadsheets. "New" creates a blank persisted
 * workbook; "Open" imports a file into a new one. Cards open `/sheets/$pageId`.
 */
import { createMemo, createSignal } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Table2 } from "@fleet/icons";
import { createDocumentPageMutation, WORKBOOK_BLOCK_TYPE } from "@folio/shared";
import { useWorkbookPagesQuery } from "@folio/shared/solid";
import { useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";
import { importCsv, importOds, importXlsx, type Workbook } from "../lib/office-io";
import { emptyWorkbook } from "../lib/workbook-ops";
import { stripExtension } from "../lib/download";
import { workbookToBlockNode } from "../lib/document-blocks";
import { DocumentLibrary } from "./document-library";

const IMPORTERS: Record<string, (file: Blob) => Promise<Workbook>> = {
  xlsx: importXlsx,
  csv: importCsv,
  ods: importOds,
};

export function SheetsLibrary() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = createSignal<false | "new" | "import">(false);

  const workbooks = useWorkbookPagesQuery({
    db: () => db(),
    drizzle,
    userId: () => userId(),
  });
  const items = createMemo(() => workbooks.data ?? []);

  const ready = () => Boolean(drizzle() && userId());

  const create = async (workbook: Workbook, title: string) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    const node = workbookToBlockNode("", workbook);
    const id = await createDocumentPageMutation(dbInstance, uid, WORKBOOK_BLOCK_TYPE, {
      title,
      content: node.content,
      text: node.text,
    });
    void navigate({ to: "/sheets/$pageId", params: { pageId: id } });
  };

  const handleNew = async () => {
    if (busy()) {
      return;
    }
    setBusy("new");
    try {
      await create(emptyWorkbook(), "");
    } catch (err) {
      console.error("[sheets] create failed:", err);
      showToast(t(msg`Could not create the spreadsheet`), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleOpenFile = async (file: File) => {
    if (busy()) {
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const importer = IMPORTERS[ext];
    if (!importer) {
      showToast(t(msg`Unsupported spreadsheet file`), "error");
      return;
    }
    setBusy("import");
    try {
      const workbook = await importer(file);
      await create(
        workbook.sheets.length > 0 ? workbook : emptyWorkbook(),
        stripExtension(file.name),
      );
    } catch (err) {
      console.error("[sheets] import failed:", err);
      showToast(t(msg`Could not import that file`), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DocumentLibrary
      testid="sheets"
      icon={Table2}
      heading={t(msg`Spreadsheets`)}
      items={items}
      isLoading={() => workbooks.isLoading}
      ready={ready}
      busy={busy}
      newLabel={t(msg`New spreadsheet`)}
      emptyTitle={t(msg`No spreadsheets yet`)}
      emptyDescription={t(msg`Create a spreadsheet or open a file to get started.`)}
      untitledLabel={t(msg`Untitled spreadsheet`)}
      accept=".xlsx,.csv,.ods"
      onNew={() => void handleNew()}
      onOpenFile={(file) => void handleOpenFile(file)}
      onOpen={(pageId) => void navigate({ to: "/sheets/$pageId", params: { pageId } })}
    />
  );
}
